import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Observable, firstValueFrom, of, Subject, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
/* import { ImageCropperComponent } from '../../../../../../libs/pn-ui/src/lib/pn-image-cropper/pn-image-cropper.component'; */
import { MatSlider, MatSliderChange } from '@angular/material/slider';
import { Router } from '@angular/router';
import { AuthErrors, AuthErrorService, AuthService, PnpUserService } from '@pn-plus/pnp-services';
import { IPostUserPhotoResponse, IUserPhotoResponse } from 'libs/pnp-services/src/lib/api/user/pnp-user.model';
import { PnImageCropperComponent } from 'libs/pn-ui/src/lib/pn-image-cropper/pn-image-cropper.component';
import { LoginService } from '../../core/services/login.service';

@Component({
    selector: 'nrwl-set-avatar',
    templateUrl: './set-avatar.component.html',
    styleUrls: ['./set-avatar.component.scss'],
})
export class SetAvatarComponent implements OnInit, AfterViewInit {
    @ViewChild('cropper') imageCropperRef: PnImageCropperComponent;
    @ViewChild('zoomSlider') zoomSlider: MatSlider;
    
    public zoom = 0;
    public isLoading = false
    public canUpload = new BehaviorSubject<boolean>(false);
    public isAfterInit = false;

    activeError: any;
    
    public $zoomValue = new BehaviorSubject<any>(1);

    constructor(
        private authService: AuthService,
        private loginService: LoginService,
        private pnpUserService: PnpUserService,
        private authErrorService: AuthErrorService,
        private changeDetectionRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.isAfterInit = true;
        //
    }

    ngAfterViewInit() {
    }

    onZoomChange($event: MatSliderChange) {
        this.$zoomValue.next($event.value ? $event.value : 1);
    }

    public onClearPhotoClicked() {
        this.activeError = AuthErrors.Success;
        this.imageCropperRef.resetImage();
    }

    onImageCropperError($event: any) {
        this.activeError = AuthErrors.Success;
        if($event === -1 || $event === -2) {
            this.activeError = $event === -1 ? AuthErrors.InvalidMimeType : AuthErrors.InvalidFileSizeLarge;
            this.canUpload.next(false);
        }
    }

    async onUploadPhotoClicked() {
        this.setBusy(true);
        this.activeError = AuthErrors.Success;
        const photo = this.imageCropperRef.getImage();
        
        if(!photo) {
            this.activeError = AuthErrors.InvalidMimeType;
            return;
        }


        const photoResponse = await firstValueFrom(this.getUserPhotoUploadUrl());
        await firstValueFrom(this.uploadPhoto(photo, photoResponse));
        await firstValueFrom(this.putUserPhoto(photoResponse));
        await firstValueFrom(this.loadUserPhoto());
        
        if(!this.activeError) {
            const authModel = this.authService.getAuthModel();
            this.loginService.navigateRegistration(authModel);
        }
        //const authModel = this.updateRegistration(photo);
        //this.loginService.navigateRegistration(authModel);

        this.setBusy(false);
    }

    completeRegistration(): Observable<any> {
        return this.pnpUserService.putHasCompletedRegistration()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse.error.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                })
            );
    }
    
    uploadPhoto(photo: string | null, photoResponse: IPostUserPhotoResponse): Observable<any> {

        if(!photo || !photoResponse) {
            return of(null);
        }

        const blob = this.b64StringtoBlob2(photo);

        return this.pnpUserService.putUserPhotoToAWS(photoResponse.uploadUrl, blob, photoResponse.contentType)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse.error.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                })
            );
    }

    putUserPhoto(photoResponse: IPostUserPhotoResponse) {
        
        if(!photoResponse || this.activeError) {
            return of(null);
        }

        return this.pnpUserService.putUserPhoto(photoResponse.key)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse.error.type);
                    return of(null);
                }),
                map((response: any) => {
                    return response;
                })
            );
    }

    loadUserPhoto(): Observable<IUserPhotoResponse | null> {
        if (this.activeError) {
            return of(null);
        }

        return this.pnpUserService.getUserPhoto()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse.error.type);
                    return of(null);
                }),
                map((userPhotoResponse: IUserPhotoResponse | null) => {
                    if(userPhotoResponse) {
                        const authModel = this.authService.getAuthModel();
                        authModel.userPhotoResponse = userPhotoResponse;
                        this.authService.updateAuthModel(authModel);
                    }
                    return userPhotoResponse;
                })
            );
    }

    getUserPhotoUploadUrl(): Observable<IPostUserPhotoResponse> {
        return this.pnpUserService.postUserPhoto()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse.error.type);
                    return of(null);
                }),
                map((postUserPhotoResponse: IPostUserPhotoResponse) => {
                    return postUserPhotoResponse;
                })
            );
    }

    async onSkipThisStepClicked() {
        this.setBusy(true);
        await this.completeRegistration();
        this.loginService.navigate('/registration-complete')
        this.setBusy(false);
    }

    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }

    // -------------------------------------------------------------------------

    public b64toFormData(b64String: string, fieldName = 'file'): FormData {
        const contentType = this.getBase64ContentType(b64String);
        const realData = this.getBase64Data(b64String);
        const blob = this.b64toBlob(realData, contentType);
        const file = new File([blob], 'provider-image.png');

        const formData = new FormData();
        formData.append(fieldName, file);
        return formData;
    }

    public getBase64ContentType(b64Data: any) {
        const block = b64Data.split(';');
        return block[0].split(':')[1];
    }

    public getBase64Data(b64Data: any) {
        const block = b64Data.split(';');
        return block[1].split(',')[1];
    }

    public b64toBlob(b64Data: any, contentType: any, sliceSize?: any) {
        contentType = contentType || '';
        sliceSize = sliceSize || 512;

        let byteCharacters = b64Data;
        try {
            byteCharacters = atob(b64Data);
        } catch (e) {
            console.log('SKIPPING: Failure to decode base64 string');
        }

        const byteArrays = [];

        for (
            let offset = 0;
            offset < byteCharacters.length;
            offset += sliceSize
        ) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    public b64toFile(b64String: string, fieldName = 'file'): File {
        const contentType = this.getBase64ContentType(b64String);
        const realData = this.getBase64Data(b64String);
        const blob = this.b64toBlob(realData, contentType);
        const file = new File([blob], 'provider-image.png');
        return file;
    }

    public b64StringtoBlob2(b64String: string, fieldName = 'file'): Blob {
        const contentType = this.getBase64ContentType(b64String);
        const realData = this.getBase64Data(b64String);
        const blob = this.b64toBlob(realData, contentType);
        return blob;
    }

    public onImageCropperFileChanged($event: any){
        this.activeError = AuthErrors.Success;
        this.canUpload.next($event ? true: false);
    }
}
